import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { authApi } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { toast } from 'react-toastify';
import { OTPVerificationData, FormErrors } from '../types';

interface OTPVerificationModalProps {
  identifier: string; // Can be phone number or email
  type: 'phone' | 'email' | 'both';
  language: 'en' | 'sw';
  onClose: () => void;
  skipAutoSend?: boolean; // If true, don't automatically send OTP (already sent during registration)
  phoneNumber?: string; // Phone number when type is 'both'
  emailAddress?: string; // Email address when type is 'both'
}

const OTPVerificationModal: React.FC<OTPVerificationModalProps> = ({ 
  identifier, 
  type, 
  language, 
  onClose, 
  skipAutoSend = false,
  phoneNumber,
  emailAddress
}) => {
  const navigate = useNavigate();
  const { t: tKey } = useTranslation();
  const { loginWithOtp } = useAuth();
  const lastSentOtpKey = useRef<string>('');
  const [otpData, setOtpData] = useState<OTPVerificationData>({
    phoneNumber: identifier,
    otp: '',
    resendTimer: 60,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const content = {
    en: {
      phone: {
        title: 'Verify Your Phone Number',
        description: 'We have sent a 6-digit verification code to',
      },
      email: {
        title: 'Verify Your Email Address',
        description: 'We have sent a 6-digit verification code to',
      },
      both: {
        title: 'Verify Your Contact Information',
        description: 'We have sent a 6-digit verification code to both your email and phone number',
        emailLabel: 'Email',
        phoneLabel: 'Phone',
      },
      otpLabel: 'Enter OTP Code',
      otpPlaceholder: 'Enter 6-digit code',
      verify: 'Verify',
      resend: 'Resend Code',
      resendIn: 'Resend in',
      seconds: 'seconds',
      otpRequired: 'OTP code is required',
      otpInvalid: 'Invalid OTP code. Please try again.',
      successTitle: 'Verification Successful!',
      successMessage: type === 'email' 
        ? 'Your email has been verified. Redirecting to dashboard...'
        : type === 'both'
        ? 'Your account has been created successfully. Redirecting to dashboard...'
        : 'Your account has been created successfully. Redirecting to dashboard...',
    },
    sw: {
      phone: {
        title: 'Thibitisha Nambari Yako ya Simu',
        description: 'Tumetuma nambari ya uthibitishaji ya tarakimu 6 kwa',
      },
      email: {
        title: 'Thibitisha Anwani Yako ya Barua Pepe',
        description: 'Tumetuma nambari ya uthibitishaji ya tarakimu 6 kwa',
      },
      both: {
        title: 'Thibitisha Taarifa Zako za Mawasiliano',
        description: 'Tumetuma nambari ya uthibitishaji ya tarakimu 6 kwa barua pepe yako na nambari ya simu',
        emailLabel: 'Barua Pepe',
        phoneLabel: 'Simu',
      },
      otpLabel: 'Weka Nambari ya OTP',
      otpPlaceholder: 'Weka nambari ya tarakimu 6',
      verify: 'Thibitisha',
      resend: 'Tuma Tena',
      resendIn: 'Tuma tena baada ya',
      seconds: 'sekunde',
      otpRequired: 'Nambari ya OTP inahitajika',
      otpInvalid: 'Nambari ya OTP si sahihi. Jaribu tena.',
      successTitle: 'Uthibitishaji Umefanikiwa!',
      successMessage: type === 'email'
        ? 'Barua pepe yako imethibitishwa. Tunaelekeza kwenye dashibodi...'
        : type === 'both'
        ? 'Akaunti yako imeundwa kwa mafanikio. Tunaelekeza kwenye dashibodi...'
        : 'Akaunti yako imeundwa kwa mafanikio. Tunaelekeza kwenye dashibodi...',
    },
  };

  const t = content[language];
  const typeContent = type === 'both' ? t.both : t[type];

  // Automatically send OTP when modal opens (only once per identifier/type combination)
  // Skip if OTP was already sent during registration
  useEffect(() => {
    if (identifier && !skipAutoSend) {
      const otpKey = `${identifier}-${type}`;
      
      // Only send if we haven't sent for this identifier/type combination
      if (lastSentOtpKey.current !== otpKey) {
        lastSentOtpKey.current = otpKey;
        authApi.sendOtp(identifier, type).catch((error) => {
          // Silently fail - user can resend if needed
          console.error('Failed to send initial OTP:', error);
        });
      }
    }
  }, [identifier, type, skipAutoSend]);

  useEffect(() => {
    if (otpData.resendTimer > 0) {
      const timer = setTimeout(() => {
        setOtpData((prev) => ({ ...prev, resendTimer: prev.resendTimer - 1 }));
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpData.resendTimer]);

  const handleResend = async () => {
    try {
      if (type === 'both') {
        // Send to both channels
        await authApi.sendOtp({ email: emailAddress || identifier, phone: phoneNumber || identifier }, 'both');
        toast.success('OTP code has been resent to your email and phone');
      } else {
        await authApi.sendOtp(identifier, type);
        toast.success(`OTP code has been resent to your ${type === 'email' ? 'email' : 'phone'}`);
      }
      setOtpData((prev) => ({ ...prev, resendTimer: 60, otp: '' }));
      setErrors({});
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      toast.error(error?.response?.data?.message || 'Failed to resend OTP. Please try again.');
    }
  };

  const validateOTP = (): boolean => {
    const newErrors: FormErrors = {};

    if (!otpData.otp.trim()) {
      newErrors.otp = t.otpRequired;
    } else if (otpData.otp.length !== 6) {
      newErrors.otp = t.otpInvalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateOTP()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const verificationIdentifier = type === 'both' ? (emailAddress || identifier) : identifier;
      const verificationType = type === 'both' ? 'email' : type;
      const result = await loginWithOtp(verificationIdentifier, otpData.otp, verificationType);
      
      if (result?.requires_2fa_setup && result?.user) {
        const u = result.user as any;
        const editPath = (u.uuid && u.slug)
          ? `/user/${u.uuid}-${u.slug}/edit?tab=account&setup_2fa=true`
          : `/user/${u.username || u.id}/edit?tab=account&setup_2fa=true`;
        toast.warning(tKey('account.two_factor.required') || 'Two-factor authentication is required for your account. Please complete setup.', {
          position: 'top-center',
          autoClose: 10000,
        });
        onClose();
        navigate(editPath, { replace: true });
        return;
      }
      
      setIsSuccess(true);
      toast.success(t.successTitle);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error('OTP verification error:', error);
      
      // Extract validation errors if available
      let errorMessage = error?.message || error?.response?.data?.message || t.otpInvalid;
      
      // If there are specific validation errors, use the first one
      if (error?.errors && typeof error.errors === 'object') {
        const firstError = Object.values(error.errors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          errorMessage = firstError[0];
        } else if (typeof firstError === 'string') {
          errorMessage = firstError;
        }
      }
      // Also check response.data.errors for Laravel validation format
      else if (error?.response?.data?.errors && typeof error.response.data.errors === 'object') {
        const errors = error.response.data.errors;
        const firstErrorKey = Object.keys(errors)[0];
        if (errors[firstErrorKey] && Array.isArray(errors[firstErrorKey]) && errors[firstErrorKey].length > 0) {
          errorMessage = errors[firstErrorKey][0];
        }
      }
      
      setErrors({ otp: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4 animate-fade-in">
        <div className="bg-card rounded-lg p-8 max-w-md w-full elevation-lg animate-scale-in text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-success" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">{t.successTitle}</h3>
          <p className="text-muted-foreground">{t.successMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-card rounded-lg p-8 max-w-md w-full elevation-lg animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">{typeContent.title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded transition-fast">
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">{typeContent.description}</p>
          {type === 'both' ? (
            <div className="space-y-2 mt-3">
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{typeContent.emailLabel}:</span>
                <span className="text-sm font-semibold text-foreground">{emailAddress || identifier}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={16} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{typeContent.phoneLabel}:</span>
                <span className="text-sm font-semibold text-foreground">{phoneNumber || identifier}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm font-semibold text-foreground">{identifier}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="text"
            label={t.otpLabel}
            placeholder={t.otpPlaceholder}
            value={otpData.otp}
            onChange={(e) => setOtpData({ ...otpData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
            error={errors.otp}
            required
          />

          <div className="flex items-center justify-between">
            {otpData.resendTimer > 0 ? (
              <p className="text-sm text-muted-foreground">
                {t.resendIn} {otpData.resendTimer} {t.seconds}
              </p>
            ) : (
              <button type="button" onClick={handleResend} className="text-sm text-primary hover:text-primary/80 font-medium transition-fast">
                {t.resend}
              </button>
            )}
          </div>

          <Button type="submit" variant="default" fullWidth loading={isLoading} iconName="Shield" iconPosition="right">
            {t.verify}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default OTPVerificationModal;